import {
	Box,
	Container,
	Flex,
	Grid,
	Heading,
	Text,
	Tooltip,
} from '@radix-ui/themes'
import { NavLink } from '@remix-run/react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { Payment } from '#app/components/common/payment'
import { NewsletterForm } from '#app/components/forms/newsletter-form/newsletter-form.tsx'
import { type Locale } from '#app/models/locale'
import { type App } from '#app/routes/_layout/component.route.tsx'
import { Copyright } from '#app/routes/_layout/copyright'
import { Funk } from '#app/routes/_layout/funk.tsx'
import { I18n } from '#app/routes/_layout/i18n'
import { ROOT_I18NEXT } from '#app/routes/_layout/route-config.ts'
import { SmartMedia } from '#app/routes/_layout/smart-media'
import { SocialMedia } from '#app/routes/_layout/social-media'

/* -------------------------------------------------------------------------------------------------
 * LinkItem
 * -----------------------------------------------------------------------------------------------*/

type LinkItemProps = {
	label: string
	locale: Locale
	to: string
	setApp: React.Dispatch<React.SetStateAction<App>>
}

const LinkItem: React.FC<LinkItemProps> = ({ label, locale, to, setApp }) => (
	<li>
		<Box asChild p="1" ml="-1">
			<NavLink
				end={to === ''}
				to={`/${locale}${to}`}
				prefetch="viewport"
				className="app-NavLink"
				onClick={() =>
					setApp((prev) => ({
						...prev,
						options: {
							...prev.options,
							redirect: null,
						},
					}))
				}
			>
				{label}
			</NavLink>
		</Box>
	</li>
)

/* -------------------------------------------------------------------------------------------------
 * Footer
 * -----------------------------------------------------------------------------------------------*/

type FooterProps = {
	app: App
	locale: Locale
	setApp: React.Dispatch<React.SetStateAction<App>>
}

const Footer: React.FC<FooterProps> = ({ locale, setApp }) => {
	const { t } = useTranslation(ROOT_I18NEXT.routeRoot)

	return (
		<Box asChild pt="6">
			<footer
				id="footer"
				className="app-Footer border-t border-gray-6 bg-gray-3"
			>
				<Container px="5">
					<Grid
						asChild
						columns={{ initial: '1', sm: '2', md: '10' }}
						py="5"
						gap="8"
					>
						<nav>
							<Flex
								direction="column"
								align={{ initial: 'center', md: 'start' }}
								gridColumn={{ md: 'span 2' }}
							>
								<Heading as="h4" weight="medium" mb="1" size="3">
									{t('label.general')}
								</Heading>
								<Flex
									asChild
									direction="column"
									align={{ initial: 'center', md: 'start' }}
								>
									<ul>
										{[
											{ label: t('label.home'), to: '' },
											{ label: t('label.blog'), to: '/blog' },
											{ label: t('label.profile'), to: '/profile' },
										].map((props) => (
											<LinkItem
												key={props.label}
												locale={locale}
												setApp={setApp}
												{...props}
											/>
										))}
									</ul>
								</Flex>
							</Flex>

							<Flex
								direction="column"
								align={{ initial: 'center', md: 'start' }}
								gridColumn={{ md: 'span 2' }}
							>
								<Heading as="h4" weight="medium" mb="1" size="3">
									{t('label.shop')}
								</Heading>
								<Flex
									asChild
									direction="column"
									align={{ initial: 'center', md: 'start' }}
								>
									<ul>
										{[
											{ label: t('label.products'), to: '/products' },
											{ label: t('label.ingredients'), to: '/ingredients' },
											{
												label: t('label.omnibusOrder'),
												to: '/blog/06d63cde-0d93-46a3-bddd-e03988804da8',
											},
											{
												label: t('label.subscription'),
												to: '/blog/90e0d643-1208-41a4-92e3-6720bd4db69e',
											},
											{ label: t('label.shipment'), to: '/shipment' },
										].map((props) => (
											<LinkItem
												key={props.label}
												locale={locale}
												setApp={setApp}
												{...props}
											/>
										))}
									</ul>
								</Flex>
							</Flex>

							<Flex
								direction="column"
								align={{ initial: 'center', md: 'start' }}
								gridColumn={{ md: 'span 2' }}
							>
								<Heading as="h4" weight="medium" mb="1" size="3">
									{t('label.legal')}
								</Heading>
								<Flex
									asChild
									direction="column"
									align={{ initial: 'center', md: 'start' }}
									gapY="1"
								>
									<ul>
										{[
											{
												label: t('label.refundPolicy'),
												to: '/legal/refund-policy',
											},
											{
												label: t('label.privacyPolicy'),
												to: '/legal/privacy-policy',
											},
											{
												label: t('label.termsOfService'),
												to: '/legal/terms-of-service',
											},
											{
												label: t('label.legalNotice'),
												to: '/legal/legal-notice',
											},
										].map((props) => (
											<LinkItem
												key={props.label}
												locale={locale}
												setApp={setApp}
												{...props}
											/>
										))}
									</ul>
								</Flex>
							</Flex>

							<Flex
								direction="column"
								align={{ initial: 'center', md: 'start' }}
								gridColumn={{ md: 'span 2' }}
							>
								<Heading as="h4" weight="medium" mb="2" size="3">
									{t('label.socialMedia')}
								</Heading>
								<SocialMedia />
							</Flex>

							<Box
								mt={{ initial: '5', md: '0' }}
								gridColumn={{ sm: 'span 2' }}
								className="justify-self-center md:justify-self-end"
							>
								<Tooltip
									content={t('label.changeLanguage')}
									side="bottom"
									sideOffset={10}
								>
									<Text>
										<I18n locale={locale} />
									</Text>
								</Tooltip>
							</Box>

							<Flex
								direction="column"
								align={{ initial: 'center', md: 'start' }}
								gridColumn={{ sm: 'span 2', md: 'span 5' }}
							>
								<Heading as="h4" weight="medium" mb="2" size="3">
									{t('label.paymentMethods')}
								</Heading>
								<Flex
									asChild
									align="center"
									justify={{ initial: 'center', sm: 'start' }}
									gapX="2"
									wrap="wrap"
								>
									<Payment />
								</Flex>
							</Flex>

							<Box gridColumn={{ sm: 'span 2', md: 'span 5' }}>
								<Heading as="h4" weight="medium" mb="5" size="3">
									{t('label.newsletter')}
								</Heading>
								<NewsletterForm locale={locale} setApp={setApp} />
							</Box>

							<div className="flex flex-col items-center sm:col-span-2 md:col-span-5 md:items-start">
								<div className="sm:col-span-2 md:col-span-10 md:-mt-5">
									<Heading as="h4" weight="medium" mb="5" size="3">
										{t('label.funk')}
									</Heading>
									<Funk />
								</div>
							</div>
						</nav>
					</Grid>
					<Flex
						direction={{ initial: 'column', sm: 'row' }}
						justify="between"
						gap="1"
						py="5"
						align={{ initial: 'center', sm: 'end' }}
					>
						<Copyright />
						<SmartMedia />
					</Flex>
				</Container>
			</footer>
		</Box>
	)
}
Footer.displayName = 'Footer'

/* -----------------------------------------------------------------------------------------------*/

export { Footer }
